import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import { apiService } from '../services/api';
import { ScanCheckResponse } from '../App';
import './Scanner.css';

interface ScannerProps {
  onScanSuccess: (response: ScanCheckResponse) => void;
}

const Scanner: React.FC<ScannerProps> = ({ onScanSuccess }) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);
  const isProcessingQrRef = useRef(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.destroy();
      }
    };
  }, []);

  const startScanning = async () => {
    console.log('startScanning called');

    try {
      console.log('Starting scanner...');
      setError('');
      setIsScanning(true);

      // Wait for the video element to be rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!videoRef.current) {
        console.log('videoRef.current is still null after waiting');
        setError('Unable to access camera element');
        setIsScanning(false);
        return;
      }

      const qrScanner = new QrScanner(
        videoRef.current,
        async (result) => {
          // Prevent duplicate callbacks from QR scanner firing multiple times
          if (isProcessingQrRef.current) return;
          isProcessingQrRef.current = true;
          setLoading(true);
          try {
            const response = await apiService.scanQRCode({
              qr_code: result.data
            });

            if (response.code === 200 && response.data) {
              // New response structure with available_slots
              onScanSuccess(response.data);
            } else {
              setError(response.message || 'QR code not recognized or invalid');
            }
          } catch (err: any) {
            setError(err.message || 'Failed to process QR code. Please try again.');
          } finally {
            setLoading(false);
            qrScanner.stop();
            setIsScanning(false);
            isProcessingQrRef.current = false;
          }
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment', // Use back camera on mobile devices
          maxScansPerSecond: 5,
        }
      );

      qrScannerRef.current = qrScanner;
      await qrScanner.start();
      console.log('QR Scanner started successfully');
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Camera access denied or not available');
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current = null;
    }
    setIsScanning(false);
  };

  const logout = () => {
    apiService.logout();
    window.location.reload();
  };

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <div className="logo-section">
          <div className="logo-brand-container">
            <img src="/images/logo_no_payoff.svg" alt="Logo" className="logo" />
            <span className="brand-text"><i>Food</i></span>
          </div>
        </div>
        <div className="header-buttons">
          <button className="report-btn" onClick={() => navigate('/report')}>
            Report
          </button>
          <button className="logout-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </div>

      <div className="scanner-content">
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {!isScanning ? (
          <div className="scanner-start">
            <div className="qr-icon">📷</div>
            <h2>Pronto per leggere il QR</h2>
            <p>Premi il pulsante qui sotto per scansionare un nuovo codice.</p>
            <button
              className="scan-button"
              onClick={() => {
                console.log('Button clicked');
                startScanning();
              }}
              disabled={loading}
            >
              {loading ? 'Elaborazione...' : 'Leggi codice'}
            </button>
          </div>
        ) : (
          <div className="scanner-active">
            <div className="video-container">
              <video ref={videoRef} className="scanner-video" />
              <div className="scanner-overlay">
                <div className="scanner-frame"></div>
              </div>
            </div>
            <p>Posiziona il codice QR all'interno del riquadro</p>
            <button className="stop-button" onClick={stopScanning}>
              Ferma scansione
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Scanner;
